import React, { useState, useCallback, useMemo } from 'react';
import { useSelector } from "react-redux";
import {
    Box,
    Typography,
    Popover,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Paper,
    Divider,
} from '@mui/material';
import { 
  ExpandMore, 
  ChevronRight 
} from '@mui/icons-material';
import { SimpleTreeView, TreeItem } from '@mui/x-tree-view';
import { useDateRangeContext } from 'contexts/DateRangeContext';
import { fetchGroupedRosterDetailsByCrewCode } from 'state/api';
import { formatTZDate } from 'state/helpers';
import FlexBetween from 'components/FlexBetween';
import RosterDetailsDialog from 'components/RosterDetailsDialog';


/* ===== SAFE HELPERS ===== */
const safeEntries = (obj) => Object.entries(obj || {});
const safeNumber = v => (typeof v === 'number' ? v : 0);

const calcSummary = (items, metricKey) => {
    let totalCrew = 0;
    let totalMetric = 0;

    for (const [, v] of items) {
        totalCrew += safeNumber(v.totalCrew);
        totalMetric += safeNumber(v[metricKey]);
    }

    return {
        totalCrew,
        totalMetric,
        perCrew: totalCrew > 0 ? totalMetric / totalCrew : 0,
    };
};

const MetricTree = React.memo(({    
    title,
    data,
    metricKey,
    metricLabel,
    level1Label, // Post / Base
    level2Label, // Base / Post
    colorFn,
}) => {
    const timeMode = useSelector((state) => state.timemode);
    const { startDate, endDate } = useDateRangeContext();
    
    const [openRosterDetailsDialog, setOpenRosterDetailsDialog] = useState(false);
    const [selectedCrewCode, setSelectedCrewCode] = useState(null);
    const [selectedCrewName, setSelectedCrewName] = useState(null);
    const [selectedCrewRosters, setSelectedCrewRosters] = useState([]);
    
    const [popoverAnchor, setPopoverAnchor] = React.useState(null);
    const [popoverDates, setPopoverDates] = React.useState([]);
    
    const sortedPopoverDates = useMemo(() => {
        return [...popoverDates].sort(
            (a, b) => new Date(a) - new Date(b)
        );
    }, [popoverDates]);
    
    const openPopover = (event, dates) => {
        setPopoverAnchor(event.currentTarget);
        setPopoverDates(dates || []);
    };
    
    const closePopover = () => {
        setPopoverAnchor(null);
        setPopoverDates([]);
    };
    
    const popoverOpen = Boolean(popoverAnchor);
    
    const [openRows, setOpenRows] = useState({});
    const toggleRow = (key) => {
        setOpenRows(prev => ({
            ...prev,
            [key]: !prev[key],
        }));
    };
    
    const handleViewCrewRosters = useCallback(async (crewCode, crewName) => {
        if (!startDate || !endDate) return;
        
        try {
            setSelectedCrewCode(crewCode);
            setSelectedCrewName(crewName);
            const result = await fetchGroupedRosterDetailsByCrewCode(crewCode, startDate, endDate);       
            setSelectedCrewRosters(result);
            setOpenRosterDetailsDialog(true);
        } catch {      
            setOpenRosterDetailsDialog(false);      
        }
    
    }, [startDate, endDate]);
      
    const handleCloseRosterDetailsDialog = () => {
        setSelectedCrewRosters([]);
        setOpenRosterDetailsDialog(false);
    };

    if (!data || Object.keys(data).length === 0) return null;

    return (
        <Paper sx={{ p: 2 }}>
            <Typography variant="h6" color="primary.dark" gutterBottom>
                {title}
            </Typography>
            <Divider sx={{ mb: 1 }} />

            <SimpleTreeView
                defaultExpandedItems={[]}
                slots={{ expandIcon: ChevronRight, collapseIcon: ExpandMore }}
            >
                {safeEntries(data).map(([level1Key, level2Obj]) => {
                    const entries = safeEntries(level2Obj);
                    const { totalCrew, totalMetric, perCrew } = calcSummary(entries, metricKey);

                    return (
                        <TreeItem
                            key={level1Key}
                            itemId={level1Key}
                            label={
                                <FlexBetween>
                                    <Box display="flex" gap={1}>
                                        <Typography variant="body2" minWidth={32}>
                                            {level1Key}:
                                        </Typography>
                                        <Typography variant="body2" textAlign="right" minWidth={25}>
                                            {totalCrew}
                                        </Typography>
                                    </Box>

                                    <Box display="flex" gap={1}>
                                        <Typography variant="body2" width={20}>
                                            {metricLabel}:
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            textAlign="right"
                                            minWidth={40}
                                            sx={colorFn ? { color: colorFn(totalMetric) } : undefined}
                                        >
                                            {totalMetric.toFixed(0)}
                                        </Typography>
                                    </Box>

                                    <Box display="flex" gap={1}>
                                        <Typography variant="body2" width={80}>
                                            {metricLabel} / Crew:
                                        </Typography>
                                        <Typography variant="body2" textAlign="right" minWidth={25}>
                                            {perCrew.toFixed(1)}
                                        </Typography>
                                    </Box>
                                </FlexBetween>
                            }
                        >
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>{level2Label}</TableCell>
                                        <TableCell align="right">Crew</TableCell>
                                        <TableCell align="right">{metricLabel}</TableCell>
                                        <TableCell align="right">{metricLabel} / Crew</TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {entries.map(([level2Key, v]) => {
                                        const rowKey = `${level1Key}-${level2Key}`;
                                        const isOpen = !!openRows[rowKey];
                                        const crews = Object.values(v.crewMap || {});

                                        return (
                                            <React.Fragment key={rowKey}>
                                                {/* ===== LEVEL 2 ROW ===== */}
                                                <TableRow
                                                    hover
                                                    sx={{ cursor: crews.length ? 'pointer' : 'default' }}
                                                    onClick={() => crews.length && toggleRow(rowKey)}
                                                >
                                                    <TableCell>
                                                        <Box display="flex" alignItems="center" gap={0.5}>
                                                            {crews.length > 0 && (
                                                                isOpen
                                                                    ? <ExpandMore fontSize="small" />
                                                                    : <ChevronRight fontSize="small" />
                                                            )}
                                                            {level2Key}
                                                        </Box>
                                                    </TableCell>

                                                    <TableCell align="right">
                                                        {safeNumber(v.totalCrew)}
                                                    </TableCell>

                                                    <TableCell align="right">
                                                        {safeNumber(v[metricKey]).toFixed(0)}
                                                    </TableCell>

                                                    <TableCell align="right">
                                                        {safeNumber(v[`${metricKey}PerCrew`]).toFixed(1)}
                                                    </TableCell>
                                                </TableRow>

                                                {/* ===== CREW ROWS ===== */}
                                                {isOpen && crews.map(c => (
                                                    <TableRow key={`${rowKey}-${c.crewCode}`}>
                                                        <TableCell colSpan={3} sx={{ pl: 6 }}>
                                                            <Typography 
                                                                variant="body2"
                                                                sx={{
                                                                    cursor: 'pointer',
                                                                    '&:hover': { color: "green.deep" },
                                                                }}
                                                                onClick={() => {
                                                                    handleViewCrewRosters(c.crewCode, c.crewName);
                                                                }}
                                                            >
                                                                {c.crewCode} - {c.crewName}
                                                            </Typography>
                                                        </TableCell>

                                                        <TableCell align="right">
                                                            <Typography
                                                                variant="body2"
                                                                color='secondary'
                                                                sx={{
                                                                    cursor: 'pointer',
                                                                    textDecoration: 'underline',
                                                                }}
                                                                onClick={(e) =>
                                                                    openPopover(
                                                                        e,
                                                                        timeMode === 'UTC'
                                                                            ? c.flightDates
                                                                            : c.flightBaseDates
                                                                    )
                                                                }
                                                            >
                                                                {c.count}
                                                            </Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </React.Fragment>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TreeItem>
                    );
                })}
            </SimpleTreeView>

            <Popover
                open={popoverOpen}
                anchorEl={popoverAnchor}
                onClose={closePopover}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}
            >
                <Box sx={{ px: 1, py: 0.5, maxWidth: 220 }}>
                    {sortedPopoverDates.length ? (
                        sortedPopoverDates.map((d, i) => (
                            <Typography
                                key={i}
                                variant="body2"
                                sx={{ whiteSpace: 'nowrap' }}
                            >
                                {formatTZDate(d)}
                            </Typography>
                        ))
                    ) : (
                        <Typography variant="body2">
                            No dates available
                        </Typography>
                    )}
                </Box>
            </Popover>

            {openRosterDetailsDialog &&
                <RosterDetailsDialog 
                    open={openRosterDetailsDialog}
                    onClose={handleCloseRosterDetailsDialog}
                    crewCode={selectedCrewCode}
                    crewName={selectedCrewName}
                    crewRosters={selectedCrewRosters}
                />
            }
        </Paper>
    );
});
    
export default MetricTree;